/*
 This file is part of GNU Taler
 Copyright (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
 */

import { chacha20poly1305 } from "@noble/ciphers/chacha.js";
import { ed25519, x25519 as nobleX25519 } from "@noble/curves/ed25519.js";
import { sha512 } from "@noble/hashes/sha2.js";
import { sha256 } from "./sha256.js";

function hmac(
  digest: (data: Uint8Array) => Uint8Array,
  blockSize: number,
  key: Uint8Array,
  message: Uint8Array,
): Uint8Array {
  if (key.byteLength > blockSize) {
    key = digest(key);
  }
  if (key.byteLength < blockSize) {
    const paddedKey = new Uint8Array(blockSize);
    paddedKey.set(key);
    key = paddedKey;
  }
  const outerKeyPad = new Uint8Array(blockSize);
  const innerKeyPad = new Uint8Array(blockSize);
  for (let i = 0; i < blockSize; i++) {
    innerKeyPad[i] = key[i] ^ 0x36;
    outerKeyPad[i] = key[i] ^ 0x5c;
  }
  const innerInput = new Uint8Array(blockSize + message.byteLength);
  innerInput.set(innerKeyPad);
  innerInput.set(message, blockSize);
  const innerHash = digest(innerInput);
  const outerInput = new Uint8Array(blockSize + innerHash.byteLength);
  outerInput.set(outerKeyPad);
  outerInput.set(innerHash, blockSize);
  return digest(outerInput);
}

export function hashSha256(data: Uint8Array): Uint8Array {
  return sha256(data);
}

export function hashSha512(data: Uint8Array): Uint8Array {
  return sha512(data);
}

export function hmacSha256(key: Uint8Array, message: Uint8Array): Uint8Array {
  return hmac(hashSha256, 64, key, message);
}

export function hmacSha512(key: Uint8Array, message: Uint8Array): Uint8Array {
  return hmac(hashSha512, 128, key, message);
}

export function eddsaGetPublic(seed: Uint8Array): Uint8Array {
  return ed25519.getPublicKey(seed);
}

export function eddsaSign(message: Uint8Array, seed: Uint8Array): Uint8Array {
  return ed25519.sign(message, seed);
}

export function eddsaVerify(
  message: Uint8Array,
  signature: Uint8Array,
  publicKey: Uint8Array,
): boolean {
  return ed25519.verify(signature, message, publicKey);
}

export function x25519GetPublic(privateKey: Uint8Array): Uint8Array {
  return nobleX25519.getPublicKey(privateKey);
}

export function x25519(
  privateKey: Uint8Array,
  publicKey: Uint8Array,
): Uint8Array {
  return nobleX25519.getSharedSecret(privateKey, publicKey);
}

export function chacha20Poly1305Encrypt(
  message: Uint8Array,
  additionalData: Uint8Array,
  nonce: Uint8Array,
  key: Uint8Array,
): Uint8Array {
  return chacha20poly1305(key, nonce, additionalData).encrypt(message);
}

export function chacha20Poly1305Decrypt(
  ciphertext: Uint8Array,
  additionalData: Uint8Array,
  nonce: Uint8Array,
  key: Uint8Array,
): Uint8Array | undefined {
  const cipher = chacha20poly1305(key, nonce, additionalData);
  try {
    return cipher.decrypt(ciphertext);
  } catch {
    return undefined;
  }
}

export interface Sha512Context {
  update(data: Uint8Array): void;
  finish(): Uint8Array;
}

export function createSha512Context(): Sha512Context {
  const hash = sha512.create();
  let digest: Uint8Array | undefined;
  return {
    update(data: Uint8Array): void {
      if (digest) {
        throw new Error("SHA-512 context is already finalized");
      }
      hash.update(data);
    },
    finish(): Uint8Array {
      digest ??= hash.digest();
      return new Uint8Array(digest);
    },
  };
}
